import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function Home() {
  useEffect(() => {
    // Update page title and meta description
    document.title = 'CzechAI - Umělá inteligence pro český byznys | AI řešení pro firmy';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Implementujeme AI řešení pro malé i velké firmy v České republice. Automatizace procesů, chatboti, analýza dat a další AI služby. Více než 150 spokojených klientů, 95% míra úspěšnosti.');
    }

    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "CzechAI",
      "description": "Implementujeme AI řešení pro malé i velké firmy v České republice",
      "url": import.meta.env.VITE_SITE_URL || "https://example.com",
      "logo": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/logo.png`,
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+420-732-941-900",
        "contactType": "customer service",
        "areaServed": "CZ",
        "availableLanguage": "Czech"
      },
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "CZ",
        "addressRegion": "Česká republika"
      },
      "sameAs": [
        `${import.meta.env.VITE_SITE_URL || "https://example.com"}/o-nas`,
        `${import.meta.env.VITE_SITE_URL || "https://example.com"}/sluzby`
      ],
      "offers": {
        "@type": "Offer",
        "description": "AI řešení pro automatizaci procesů, chatboti, analýza dat",
        "category": "Umělá inteligence a automatizace"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "reviewCount": "150",
        "bestRating": "5"
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const [fontSettings, setFontSettings] = useState({
    family: 'Inter',
    weight: '500',
    size: 'base',
    letterSpacing: 'normal',
    lineHeight: 'normal'
  });

  const [colorSettings, setColorSettings] = useState({
    primary: 'gray',
    background: 'white',
    accent: 'gray'
  });

  const [showFontPanel, setShowFontPanel] = useState(false);
  const [showColorPanel, setShowColorPanel] = useState(false);
  const [showImagePanel, setShowImagePanel] = useState(false);
  const [showKonfigurator, setShowKonfigurator] = useState(false);

  const benefits = [
    {
      icon: 'ri-time-line',
      title: 'Úspora času',
      description: 'Automatizace rutinních úkolů uleví menším týmům i velkým oddělením'
    },
    {
      icon: 'ri-lightbulb-line',
      title: 'Chytřejší rozhodování',
      description: 'AI analýzy pomáhají živnostníkům i korporacím dělat přesnější a rychlejší rozhodnutí'
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Nepřetržitý zákaznický servis',
      description: 'Virtuální asistenti zvládnou podporu klientů nonstop – bez ohledu na velikost firmy'
    },
    {
      icon: 'ri-magic-line',
      title: 'Řešení na míru',
      description: 'Ať už jste startup, rodinná firma nebo mezinárodní podnik, přizpůsobíme AI vašim potřebám'
    }
  ];

  const useCases = [
    {
      icon: 'ri-shopping-bag-3-line',
      title: 'E-commerce a retail',
      description: 'E-shopy, online marketplace, maloobchodní řetězce, lokální prodejny, HoReCa (restaurace, hotely, catering)'
    },
    {
      icon: 'ri-tools-line',
      title: 'Výroba a průmysl',
      description: 'Kovovýroba, CNC obrábění, strojírenství, automotive dodavatelé, dřevozpracující a nábytkářské firmy, textilní a oděvní průmysl, potravinářství a zemědělské podniky'
    },
    {
      icon: 'ri-headphone-line',
      title: 'Služby a zákaznický servis',
      description: 'Callcentra a kontaktní centra, servisní společnosti, autoservisy, zdravotnictví (soukromé kliniky, ambulance), wellness, fitness, beauty'
    },
    {
      icon: 'ri-bank-line',
      title: 'Finance a reality',
      description: 'Banky, pojišťovny, fintech společnosti, realitní kanceláře, developeři, investiční fondy'
    },
    {
      icon: 'ri-team-line',
      title: 'HR a nábor',
      description: 'Personální agentury, společnosti s vysokou fluktuací zaměstnanců (výroba, retail, logistika), interní HR oddělení velkých firem'
    },
    {
      icon: 'ri-truck-line',
      title: 'Logistika a doprava',
      description: 'Dopravní a spediční firmy, skladové společnosti, kurýrní služby, veřejná doprava'
    },
    {
      icon: 'ri-flashlight-line',
      title: 'Energetika a utility',
      description: 'Energetické společnosti, dodavatelé energií, teplárenství, vodárny, komunální služby'
    },
    {
      icon: 'ri-hammer-line',
      title: 'Tradiční a rodinné podniky',
      description: 'Sklářství, keramika, textil, kožené výrobky, řemesla a menší rodinné podniky'
    },
    {
      icon: 'ri-graduation-cap-line',
      title: 'Školství a vzdělávání',
      description: 'Základní a střední školy, univerzity, jazykové školy, soukromé vzdělávací instituce'
    },
    {
      icon: 'ri-government-line',
      title: 'Stát a veřejný sektor',
      description: 'Městské úřady, krajské samosprávy, státní podniky, veřejné instituce, neziskové organizace'
    }
  ];

  const fontFamilies = [
    { name: 'Inter', value: 'Inter' },
    { name: 'Orbitron', value: 'Orbitron' },
    { name: 'Exo 2', value: 'Exo 2' },
    { name: 'Rajdhani', value: 'Rajdhani' },
    { name: 'Space Grotesk', value: 'Space Grotesk' }
  ];

  const fontWeights = [
    { name: 'Tenké', value: '300' },
    { name: 'Normální', value: '400' },
    { name: 'Střední', value: '500' },
    { name: 'Tučné', value: '600' },
    { name: 'Extra tučné', value: '700' },
    { name: 'Ultra tučné', value: '900' }
  ];

  const fontSizes = [
    { name: 'Malé', value: 'sm' },
    { name: 'Normální', value: 'base' },
    { name: 'Velké', value: 'lg' },
    { name: 'Extra velké', value: 'xl' },
    { name: 'XXL', value: '2xl' }
  ];

  const letterSpacings = [
    { name: 'Úzké', value: 'tighter' },
    { name: 'Normální', value: 'normal' },
    { name: 'Široké', value: 'wide' },
    { name: 'Extra široké', value: 'wider' }
  ];

  const lineHeights = [
    { name: 'Úzké', value: 'tight' },
    { name: 'Normální', value: 'normal' },
    { name: 'Volné', value: 'relaxed' },
    { name: 'Extra volné', value: 'loose' }
  ];

  const colorSchemes = [
    { name: 'Černá', primary: 'gray', accent: 'gray' },
    { name: 'Tmavě šedá', primary: 'gray', accent: 'gray' }
  ];

  const backgroundOptions = [
    { name: 'Bílá', value: 'white' },
    { name: 'Světle šedá', value: 'gray-50' }
  ];

  const imageStyles = [
    { name: 'Černobílý', query: 'Modern professional business office with advanced technology, AI robots and computers, clean black and white corporate environment, minimalist design, Czech business professionals working with artificial intelligence systems, high-tech workspace, monochrome atmosphere, clean lighting, grayscale' },
    { name: 'Technologický', query: 'Futuristic technology background with AI neural networks, digital circuits, holographic displays, advanced computer systems, black and white tech environment, professional modern workspace with artificial intelligence elements, monochrome, grayscale' },
    { name: 'Inovativní', query: 'Creative innovation workspace with modern design, startup atmosphere, bright lighting, collaborative environment, technology and creativity merged, contemporary office space with AI elements, inspiring professional setting, black and white, monochrome' },
    { name: 'Elegantní', query: 'Elegant luxury business environment, sophisticated office design, premium materials, clean lines, professional atmosphere, high-end technology integration, minimalist luxury workspace with subtle AI elements, black and white, grayscale' }
  ];

  const getTextClasses = () => {
    const weightMap = {
      '300': 'font-light',
      '400': 'font-normal',
      '500': 'font-medium',
      '600': 'font-semibold',
      '700': 'font-bold',
      '900': 'font-black'
    };

    const sizeMap = {
      'sm': 'text-sm',
      'base': 'text-base',
      'lg': 'text-lg',
      'xl': 'text-xl',
      '2xl': 'text-2xl'
    };

    const spacingMap = {
      'tighter': 'tracking-tighter',
      'normal': 'tracking-normal',
      'wide': 'tracking-wide',
      'wider': 'tracking-wider'
    };

    const heightMap = {
      'tight': 'leading-tight',
      'normal': 'leading-normal',
      'relaxed': 'leading-relaxed',
      'loose': 'leading-loose'
    };

    return `${weightMap[fontSettings.weight]} ${sizeMap[fontSettings.size]} ${spacingMap[fontSettings.letterSpacing]} ${heightMap[fontSettings.lineHeight]}`;
  };

  const getFontFamily = () => {
    const familyMap = {
      'Inter': '"Inter", sans-serif',
      'Orbitron': '"Orbitron", monospace',
      'Exo 2': '"Exo 2", sans-serif',
      'Rajdhani': '"Rajdhani", sans-serif',
      'Space Grotesk': '"Space Grotesk", sans-serif'
    };
    return familyMap[fontSettings.family];
  };

  const [currentImageStyle, setCurrentImageStyle] = useState(imageStyles[0]);

  return (
    <div className={`min-h-screen bg-${colorSettings.background}`} style={{ fontFamily: getFontFamily() }}>
      <Navigation />
      
      {/* Font Settings Panel */}
      <div className={`fixed top-20 right-4 z-50 bg-white rounded-xl shadow-2xl border p-6 w-80 transition-transform duration-300 ${showFontPanel ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Nastavení písma</h3>
          <button onClick={() => setShowFontPanel(false)} className="w-6 h-6 flex items-center justify-center cursor-pointer">
            <i className="ri-close-line text-gray-600"></i>
          </button>
        </div>

        <div className="space-y-4">
          {/* Font Family */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Rodina písma</label>
            <select 
              value={fontSettings.family}
              onChange={(e) => setFontSettings({...fontSettings, family: e.target.value})}
              className="w-full p-2 border border-gray-300 rounded-lg text-sm pr-8"
            >
              {fontFamilies.map(font => (
                <option key={font.value} value={font.value}>{font.name}</option>
              ))}
            </select>
          </div>

          {/* Font Weight */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tloušťka písma</label>
            <select 
              value={fontSettings.weight}
              onChange={(e) => setFontSettings({...fontSettings, weight: e.target.value})}
              className="w-full p-2 border border-gray-300 rounded-lg text-sm pr-8"
            >
              {fontWeights.map(weight => (
                <option key={weight.value} value={weight.value}>{weight.name}</option>
              ))}
            </select>
          </div>

          {/* Font Size */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Velikost písma</label>
            <select 
              value={fontSettings.size}
              onChange={(e) => setFontSettings({...fontSettings, size: e.target.value})}
              className="w-full p-2 border border-gray-300 rounded-lg text-sm pr-8"
            >
              {fontSizes.map(size => (
                <option key={size.value} value={size.value}>{size.name}</option>
              ))}
            </select>
          </div>

          {/* Letter Spacing */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Rozestup písmen</label>
            <select 
              value={fontSettings.letterSpacing}
              onChange={(e) => setFontSettings({...fontSettings, letterSpacing: e.target.value})}
              className="w-full p-2 border border-gray-300 rounded-lg text-sm pr-8"
            >
              {letterSpacings.map(spacing => (
                <option key={spacing.value} value={spacing.value}>{spacing.name}</option>
              ))}
            </select>
          </div>

          {/* Line Height */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Výška řádku</label>
            <select 
              value={fontSettings.lineHeight}
              onChange={(e) => setFontSettings({...fontSettings, lineHeight: e.target.value})}
              className="w-full p-2 border border-gray-300 rounded-lg text-sm pr-8"
            >
              {lineHeights.map(height => (
                <option key={height.value} value={height.value}>{height.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Color Settings Panel */}
      <div className={`fixed top-20 right-4 z-50 bg-white rounded-xl shadow-2xl border p-6 w-80 transition-transform duration-300 ${showColorPanel ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Nastavení barev</h3>
          <button onClick={() => setShowColorPanel(false)} className="w-6 h-6 flex items-center justify-center cursor-pointer">
            <i className="ri-close-line text-gray-600"></i>
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Barevné schéma</label>
            <div className="grid grid-cols-2 gap-2">
              {colorSchemes.map(scheme => (
                <button
                  key={scheme.name}
                  onClick={() => setColorSettings({...colorSettings, primary: scheme.primary})}
                  className={`p-3 rounded-lg border text-sm ${colorSettings.primary === scheme.primary ? 'border-gray-400 bg-gray-50' : 'border-gray-200'} cursor-pointer`}
                >
                  <div className={`w-4 h-4 bg-black rounded-full mb-1 mx-auto`}></div>
                  {scheme.name}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Pozadí stránky</label>
            <select 
              value={colorSettings.background}
              onChange={(e) => setColorSettings({...colorSettings, background: e.target.value})}
              className="w-full p-2 border border-gray-300 rounded-lg text-sm pr-8"
            >
              {backgroundOptions.map(bg => (
                <option key={bg.value} value={bg.value}>{bg.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Image Settings Panel */}
      <div className={`fixed top-20 right-4 z-50 bg-white rounded-xl shadow-2xl border p-6 w-80 transition-transform duration-300 ${showImagePanel ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Nastavení obrázků</h3>
          <button onClick={() => setShowImagePanel(false)} className="w-6 h-6 flex items-center justify-center cursor-pointer">
            <i className="ri-close-line text-gray-600"></i>
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Styl hlavního obrázku</label>
            <div className="space-y-2">
              {imageStyles.map(style => (
                <button
                  key={style.name}
                  onClick={() => setCurrentImageStyle(style)}
                  className={`w-full p-3 text-left rounded-lg border text-sm ${currentImageStyle.name === style.name ? 'border-gray-400 bg-gray-50' : 'border-gray-200'} cursor-pointer`}
                >
                  {style.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Control Buttons */}
      <div className="fixed top-20 right-4 z-40 flex flex-col gap-2">
        <button 
          onClick={() => {
            setShowColorPanel(false);
            setShowImagePanel(false);
            setShowFontPanel(!showFontPanel);
          }}
          className={`bg-black text-white p-3 rounded-full shadow-lg hover:bg-gray-800 transition-colors cursor-pointer`}
        >
          <i className="ri-font-size-2 text-lg"></i>
        </button>
        <button 
          onClick={() => {
            setShowFontPanel(false);
            setShowImagePanel(false);
            setShowColorPanel(!showColorPanel);
          }}
          className={`bg-black text-white p-3 rounded-full shadow-lg hover:bg-gray-800 transition-colors cursor-pointer`}
        >
          <i className="ri-palette-line text-lg"></i>
        </button>
        <button 
          onClick={() => {
            setShowFontPanel(false);
            setShowColorPanel(false);
            setShowImagePanel(!showImagePanel);
          }}
          className={`bg-black text-white p-3 rounded-full shadow-lg hover:bg-gray-800 transition-colors cursor-pointer`}
        >
          <i className="ri-image-line text-lg"></i>
        </button>
      </div>
      
      {/* Hero sekce */}
      <section className="relative">
        {/* Hlavní obrázek - celý viditelný */}
        <div className="w-full h-auto relative">
          <img 
            src="https://static.readdy.ai/image/2e0b1e22c1b74afa84b035f494e9bd64/22ebae5a1eb34ef8900c0564c87dce22.png"
            alt="AI technologie"
            className="w-full h-auto object-contain"
          />

          {/* Platform Buttons Overlay - levý horní roh na wallpaperu */}
          <div className="absolute top-6 left-6 flex flex-col gap-3 z-20">
            {[
              { label: 'PLATFORMA', url: 'https://chat.czechai.io', icon: 'ri-chat-ai-line' },
              { label: 'VIP', url: 'https://vip.czechai.io', icon: 'ri-vip-crown-line' },
              { label: 'LEADY', url: 'https://leads.czechai.io', icon: 'ri-bar-chart-grouped-line' },
            ].map((btn) => (
              <a
                key={btn.label}
                href={btn.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative flex items-center gap-2.5 px-5 py-3 rounded-xl font-bold text-sm tracking-widest text-white cursor-pointer overflow-hidden marble-glow"
                style={{ minWidth: '170px' }}
              >
                <span className="marble-border" aria-hidden="true"></span>
                <span className="absolute inset-[2px] rounded-[10px] bg-black/85 backdrop-blur-sm z-10"></span>
                <span className="relative z-20 flex items-center gap-2.5 w-full">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className={`${btn.icon} text-base`}></i>
                  </span>
                  <span className="flex-1">{btn.label}</span>
                  <span className="w-4 h-4 flex items-center justify-center opacity-50 group-hover:opacity-100 transition-opacity">
                    <i className="ri-arrow-right-up-line text-xs"></i>
                  </span>
                </span>
              </a>
            ))}
          </div>
        </div>
        
        {/* Černý pruh s obsahem */}
        <div className="bg-black py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className={`text-4xl md:text-6xl font-bold text-white mb-6 leading-tight ${getTextClasses()}`} contentEditable suppressContentEditableWarning>
                Umělá inteligence pro
                <span className="text-white"> český byznys</span>
              </h1>
              <p className={`text-xl text-gray-300 mb-8 leading-relaxed ${getTextClasses()}`} contentEditable suppressContentEditableWarning>
                Pomáháme malým i velkým firmám implementovat AI řešení, která šetří čas, 
                snižují náklady a zvyšují konkurenceschopnost. Začněte svou digitální transformaci už dnes.
              </p>
              <div className="flex justify-center space-x-4 mb-8">
                <Link to="/kontakt">
                  <Button size="lg" className="border-2 border-white text-white bg-transparent hover:bg-white hover:text-black">
                    <i className="ri-phone-line mr-2"></i>
                    <span contentEditable suppressContentEditableWarning>Nezávazná konzultace</span>
                  </Button>
                </Link>
                <Link to="/kontakt">
                  <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-black">
                    <i className="ri-mail-line mr-2"></i>
                    <span contentEditable suppressContentEditableWarning>Kontaktní formulář</span>
                  </Button>
                </Link>
              </div>


              {/* AI Konfigurátor CTA */}
              <div className="mt-8 mb-4">
                <div
                  onClick={() => setShowKonfigurator(true)}
                  className="group cursor-pointer mx-auto max-w-2xl bg-gradient-to-r from-amber-500/20 via-yellow-500/10 to-amber-500/20 border border-amber-500/30 rounded-2xl p-6 hover:border-amber-400/60 transition-all hover:scale-[1.02]"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-amber-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-robot-2-line text-2xl text-amber-400"></i>
                    </div>
                    <div className="text-left">
                      <p className="text-white font-bold text-lg">Kde vám ve firmě utíkají peníze?</p>
                      <p className="text-gray-400 text-sm mt-1">Zjistěte za 10 vteřin, kde by AI implementace nejvíce pomohla. Vyzkoušejte náš agentní konfigurátor.</p>
                    </div>
                    <i className="ri-arrow-right-line text-2xl text-amber-400 group-hover:translate-x-1 transition-transform flex-shrink-0"></i>
                  </div>
                </div>
              </div>

              {showKonfigurator && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowKonfigurator(false)}>
                  <div className="bg-white rounded-2xl w-full max-w-4xl h-[85vh] overflow-hidden shadow-2xl relative" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center">
                          <i className="ri-robot-2-line text-white"></i>
                        </div>
                        <span className="font-bold text-gray-900">CzechAI Konfigurátor</span>
                      </div>
                      <button onClick={() => setShowKonfigurator(false)} className="w-8 h-8 rounded-lg bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-gray-600">&#10005;</button>
                    </div>
                    <iframe src="https://novamaris.replit.app/czechai" className="w-full border-0" style={{height: 'calc(85vh - 60px)'}} title="CzechAI AI Konfigurátor" />
                  </div>
                </div>
              )}

              {/* Velké AI Chat tlačítko */}
              <div className="flex justify-center">
                <button
                  onClick={() => {
                    const btn = document.querySelector('#vapi-widget-floating-button') as HTMLElement | null;
                    if (btn) btn.click();
                  }}
                  className="group relative flex items-center px-10 py-5 rounded-2xl font-semibold text-base tracking-wide text-white cursor-pointer overflow-hidden transition-transform hover:scale-105 marble-glow"
                  style={{ minWidth: '320px' }}
                >
                  <span className="marble-border" aria-hidden="true"></span>
                  <span className="absolute inset-[2px] rounded-[14px] bg-black/90 z-10"></span>
                  <span className="relative z-20 flex items-center gap-4 w-full justify-center">
                    <span className="w-11 h-11 flex items-center justify-center bg-white/10 rounded-xl flex-shrink-0">
                      <i className="ri-robot-2-line text-2xl"></i>
                    </span>
                    <span className="flex flex-col text-left">
                      <span className="text-base font-bold leading-tight">Zeptejte se AI asistenta</span>
                      <span className="text-xs text-gray-400 font-normal mt-0.5">Online · Odpovídám ihned</span>
                    </span>
                    <span className="ml-3 w-7 h-7 flex items-center justify-center bg-white/10 rounded-full group-hover:bg-white/20 transition-colors flex-shrink-0">
                      <i className="ri-arrow-right-line text-sm"></i>
                    </span>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Přínosy */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className={`text-3xl md:text-4xl font-bold text-gray-900 mb-4 ${getTextClasses()}`} contentEditable suppressContentEditableWarning>
              Proč zvolit AI řešení od CzechAi?
            </h2>
            <p className={`text-xl text-gray-600 max-w-3xl mx-auto ${getTextClasses()}`} contentEditable suppressContentEditableWarning>
              Naše klienti dosahují měřitelných výsledků již během prvních měsíců implementace
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className={`w-16 h-16 bg-white border-2 border-black rounded-xl flex items-center justify-center mb-6 shadow-lg`}>
                  <i className={`${benefit.icon} text-2xl text-black`}></i>
                </div>
                <h3 className={`text-xl font-bold text-gray-900 mb-4 ${getTextClasses()}`} contentEditable suppressContentEditableWarning>{benefit.title}</h3>
                <p className={`text-gray-600 leading-relaxed ${getTextClasses()}`} contentEditable suppressContentEditableWarning>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Oblasti využití */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className={`text-3xl md:text-4xl font-bold text-gray-900 mb-4 ${getTextClasses()}`} contentEditable suppressContentEditableWarning>
              Kde vám AI pomůže
            </h2>
            <p className={`text-xl text-gray-600 max-w-3xl mx-auto ${getTextClasses()}`} contentEditable suppressContentEditableWarning>
              Implementujeme AI řešení napříč všemi oblastmi vašeho podnikání
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
            {useCases.map((useCase, index) => (
              <Link 
                key={index} 
                to={`/sluzby#${
                  useCase.title.includes('E-commerce') ? 'marketing-prodej' :
                  useCase.title.includes('Výroba') ? 'automatizace-procesu' :
                  useCase.title.includes('Služby a zákaznický') ? 'chatboti-servis' :
                  useCase.title.includes('Finance') ? 'analytika-bi' :
                  useCase.title.includes('HR') ? 'skoleni-rozvoj' :
                  useCase.title.includes('Logistika') ? 'automatizace-monitoring' :
                  useCase.title.includes('Energetika') ? 'automatizace-monitoring' :
                  useCase.title.includes('Tradiční') ? 'automatizace-procesu' :
                  useCase.title.includes('Školství') ? 'skoleni-rozvoj' :
                  'odborne-sluzby'
                }`}
                className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-shadow cursor-pointer block"
              >
                <div className="w-16 h-16 bg-white border-2 border-black rounded-xl flex items-center justify-center mb-4 shadow-lg">
                  <i className={`${useCase.icon} text-2xl text-black`}></i>
                </div>
                <h3 className={`text-lg font-semibold text-gray-900 mb-3 ${getTextClasses()}`} contentEditable suppressContentEditableWarning>{useCase.title}</h3>
                <p className={`text-gray-600 text-sm leading-relaxed ${getTextClasses()}`} contentEditable suppressContentEditableWarning>{useCase.description}</p>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/sluzby#obchod-zakaznici">
              <Button size="lg" variant="outline" className={`border-black text-black hover:bg-black hover:text-white`}>
                <span contentEditable suppressContentEditableWarning>Zobrazit všechny služby</span>
                <i className="ri-arrow-right-line ml-2"></i>
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Statistiky sekce */}
      <section className="py-20 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Důvěřuje nám přes 150 českých firem
            </h2>
            <p className="text-xl text-gray-300">
              Od startupů po korporace - pomáháme růst všem
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center opacity-60">
            <div className="text-center">
              <div className="text-3xl font-bold text-white mb-2">150+</div>
              <div className="text-sm text-gray-400">Spokojených klientů</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white mb-2">95%</div>
              <div className="text-sm text-gray-400">Míra spokojenosti</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white mb-2">500+</div>
              <div className="text-sm text-gray-400">Implementovaných řešení</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white mb-2">24/7</div>
              <div className="text-sm text-gray-400">Technická podpora</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA sekce */}
      <section className={`py-20 bg-white`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className={`text-3xl md:text-4xl font-bold text-black mb-6 ${getTextClasses()}`} contentEditable suppressContentEditableWarning>
            Jste připraveni na změnu?
          </h2>
          <p className={`text-xl text-gray-600 mb-8 ${getTextClasses()}`} contentEditable suppressContentEditableWarning>
            Nechte si připravit nezávaznou analýzu možností AI pro vaši firmu
          </p>
          <div className="flex justify-center">
            <Link to="/kontakt">
              <Button size="lg" className="bg-black text-white border-2 border-white hover:bg-white hover:text-black whitespace-nowrap">
                <i className="ri-calendar-line mr-2"></i>
                <span contentEditable suppressContentEditableWarning>Objednat konzultaci</span>
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
